
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

interface DeviceEditData {
  device_id: string;
  device_name: string | null;
  device_description: string | null;
  images_allowed: number;
  images_current: number;
  camera_image_quality?: string | null;
  group_name?: string | null;
  group_reference?: string | null;
}

export const useDeviceEdit = () => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const { logChange } = useDeviceChangeLog();

  const updateDeviceDetails = async (data: DeviceEditData) => {
    setIsEditing(true);
    try {
      // Prepare update data - exclude images_current from direct update
      const updateData: any = {
        device_name: data.device_name,
        device_description: data.device_description,
        images_allowed: data.images_allowed,
        group_name: data.group_name,
        group_reference: data.group_reference,
      };
      
      // Add camera_image_quality if value is provided
      if (data.camera_image_quality !== undefined) {
        updateData.camera_image_quality = data.camera_image_quality;
      }
      
      // Log what we're updating
      console.log('Updating device details in database:', data.device_id, updateData);
      
      const { error: dbError } = await supabase
        .from("device_details")
        .update(updateData)
        .eq("device_id", data.device_id);

      if (dbError) throw dbError;

      // Update images_current separately if needed using RPC
      if (data.images_current !== undefined) {
        // Use type assertion for custom RPC call
        await (supabase as any).rpc('update_device_image_count', {
          device_id_param: data.device_id,
          new_count: data.images_current
        });
      }

      // Update the contract via API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

      // Update device name if provided
      if (data.device_name !== null) {
        const paramsName = new URLSearchParams();
        paramsName.append("deviceID", data.device_id);
        paramsName.append("field", "deviceName");
        paramsName.append("value", data.device_name);

        console.log('Updating device name:', data.device_id, data.device_name);
        logChange(data.device_id, "deviceName", data.device_name || "");
        const responseName = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: paramsName,
        });

        if (!responseName.ok) {
          console.error('Failed to update device name:', await responseName.text());
          throw new Error("Failed to update device name");
        }
      }

      // Update device description if provided
      if (data.device_description !== null) {
        const paramsDesc = new URLSearchParams();
        paramsDesc.append("deviceID", data.device_id);
        paramsDesc.append("field", "deviceDescription");
        paramsDesc.append("value", data.device_description);

        console.log('Updating device description:', data.device_id, data.device_description);
        logChange(data.device_id, "deviceDescription", data.device_description || "");
        const responseDesc = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: paramsDesc,
        });

        if (!responseDesc.ok) {
          console.error('Failed to update device description:', await responseDesc.text());
          throw new Error("Failed to update device description");
        }
      }

      // Update camera image quality if provided
      if (data.camera_image_quality !== undefined) {
        const paramsCameraQuality = new URLSearchParams();
        paramsCameraQuality.append("deviceID", data.device_id);
        paramsCameraQuality.append("field", "cameraConfig.cameraImageQuality");
        paramsCameraQuality.append("value", data.camera_image_quality || "");

        console.log('Updating camera image quality via API:', data.device_id, data.camera_image_quality);
        logChange(data.device_id, "cameraConfig.cameraImageQuality", data.camera_image_quality || "");
        const responseCameraQuality = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: paramsCameraQuality,
        });

        if (!responseCameraQuality.ok) {
          console.error('Failed to update camera image quality:', await responseCameraQuality.text());
          throw new Error("Failed to update camera image quality");
        } else {
          const responseText = await responseCameraQuality.text();
          console.log('Camera image quality update response:', responseText);
        }
      }

      // Update group name if provided
      if (data.group_name !== undefined) {
        const paramsGroupName = new URLSearchParams();
        paramsGroupName.append("deviceID", data.device_id);
        paramsGroupName.append("field", "groupName");
        paramsGroupName.append("value", data.group_name || "");

        console.log('Updating group name via API:', data.device_id, data.group_name);
        logChange(data.device_id, "groupName", data.group_name || "");
        const responseGroupName = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: paramsGroupName,
        });

        if (!responseGroupName.ok) {
          console.error('Failed to update group name:', await responseGroupName.text());
          throw new Error("Failed to update group name");
        }
      }

      // Update group reference if provided
      if (data.group_reference !== undefined) {
        const paramsGroupRef = new URLSearchParams();
        paramsGroupRef.append("deviceID", data.device_id);
        paramsGroupRef.append("field", "groupReference");
        paramsGroupRef.append("value", data.group_reference || "");

        console.log('Updating group reference via API:', data.device_id, data.group_reference);
        logChange(data.device_id, "groupReference", data.group_reference || "");
        const responseGroupRef = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: paramsGroupRef,
        });

        if (!responseGroupRef.ok) {
          console.error('Failed to update group reference:', await responseGroupRef.text());
          throw new Error("Failed to update group reference");
        }
      }

      // Update images allowed
      const paramsAllowed = new URLSearchParams();
      paramsAllowed.append("deviceID", data.device_id);
      paramsAllowed.append("field", "contract.imagesAllowed");
      paramsAllowed.append("value", data.images_allowed.toString());

      console.log('Updating images allowed:', data.device_id, data.images_allowed);
      logChange(data.device_id, "contract.imagesAllowed", data.images_allowed.toString());
      const responseAllowed = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: paramsAllowed,
      });

      if (!responseAllowed.ok) {
        console.error('Failed to update images allowed:', await responseAllowed.text());
        throw new Error("Failed to update device contract images allowed");
      }

      // Update images current
      const paramsCurrent = new URLSearchParams();
      paramsCurrent.append("deviceID", data.device_id);
      paramsCurrent.append("field", "contract.imagesCurrent");
      paramsCurrent.append("value", data.images_current.toString());

      console.log('Updating images current:', data.device_id, data.images_current);
      logChange(data.device_id, "contract.imagesCurrent", data.images_current.toString());
      const responseCurrent = await fetch(`https://i56o7w.buildship.run/updateDevice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: paramsCurrent,
      });

      if (!responseCurrent.ok) {
        console.error('Failed to update images current:', await responseCurrent.text());
        throw new Error("Failed to update device contract images current");
      }

      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ["device-details"] });
      
      toast.success("Device details updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating device:", error);
      toast.error("Failed to update device details");
      return false;
    } finally {
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    updateDeviceDetails
  };
};
